import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, DASHBOARD_ROUTES } from '../context/AuthContext';
import './VerifyEmailPage.css';

export default function VerifyEmailPage({ mode }) {
  const { user, verifyEmail, resendVerification, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);

  // Handle /verify?token=... links from email
  useEffect(() => {
    if (mode === 'confirm') {
      const token = searchParams.get('token');
      if (token) {
        verifyEmail(token).then(() => setVerified(true));
      }
    }
  }, [mode, searchParams, verifyEmail]);

  // After verification confirmed, redirect to dashboard
  useEffect(() => {
    if (verified && user?.isEmailVerified) {
      const dest = DASHBOARD_ROUTES[user.role] || '/';
      const timer = setTimeout(() => navigate(dest), 2000);
      return () => clearTimeout(timer);
    }
  }, [verified, user, navigate]);

  const handleResend = async () => {
    if (!user) return;
    setResending(true);
    await resendVerification(user.email);
    setResending(false);
    setResent(true);
    setTimeout(() => setResent(false), 5000);
  };

  const handleLogout = () => { logout(); navigate('/'); };

  // ── Confirmed state (arrived from email link) ──────────────
  if (verified) {
    return (
      <div className="vep-wrap">
        <div className="vep-card">
          <div className="vep-icon vep-icon--success">✓</div>
          <h1 className="vep-title">Email Verified!</h1>
          <p className="vep-sub">Your email has been verified successfully. Redirecting you to your dashboard…</p>
          <div className="vep-spinner" />
        </div>
      </div>
    );
  }

  // ── Pending state (just registered, waiting for verification) ──
  return (
    <div className="vep-wrap">
      <div className="vep-card">
        <div className="vep-icon">✉</div>
        <h1 className="vep-title">Check your inbox</h1>
        <p className="vep-sub">
          We sent a verification link to{' '}
          <strong>{user?.email || 'your email'}</strong>.
          <br />Click the link in the email to activate your account.
        </p>

        <div className="vep-steps">
          <div className="vep-step"><span className="vep-num">1</span>Open the email from DevBrains</div>
          <div className="vep-step"><span className="vep-num">2</span>Click the "Verify Email" button</div>
          <div className="vep-step"><span className="vep-num">3</span>You'll be redirected to your dashboard</div>
        </div>

        {resent && (
          <div className="vep-banner vep-banner--ok">Verification email resent! Check your inbox.</div>
        )}

        <div className="vep-actions">
          <button className="vep-btn vep-btn--primary" onClick={handleResend} disabled={resending}>
            {resending ? 'Sending…' : 'Resend Verification Email'}
          </button>

          {/* Dev-only shortcut: simulate clicking the email link */}
          {process.env.NODE_ENV !== 'production' && (
            <button
              className="vep-btn vep-btn--dev"
              onClick={() => { verifyEmail('mock_token').then(() => setVerified(true)); }}
            >
              [Dev] Simulate Email Click
            </button>
          )}
        </div>

        <button className="vep-signout" onClick={handleLogout}>Sign out</button>
      </div>
    </div>
  );
}
